import { useEffect, useRef, useCallback } from 'react';
import useRealtimeSubscription, { createTableChannel } from './useRealtimeSubscription';

/**
 * Auto-refresh data when the database changes — no page refresh needed.
 * Matches Administrator app pattern for fast, responsive data.
 * - Realtime: subscribes to table changes and refetches immediately when data changes
 * - Visibility: refetches when the user switches back to the tab
 * - Fallback poll: slower poll when realtime is used, faster when not
 *
 * @param {Function} loadData - Async callback to refetch data
 * @param {Object} options - Optional config
 * @param {string[]} options.realtimeTables - Table IDs to subscribe for instant updates (e.g. [ORDERS_TABLE_ID])
 * @param {number} options.intervalMs - Polling interval when no realtime (default: 60000). With realtime, used as fallback (default: 180000)
 * @param {boolean} options.enabled - Whether auto-refresh is enabled (default: true)
 */
const useAutoRefresh = (loadData, options = {}) => {
  const {
    realtimeTables,
    intervalMs,
    enabled = true,
  } = options;

  const loadDataRef = useRef(loadData);
  const intervalRef = useRef(null);

  loadDataRef.current = loadData;

  const refresh = useCallback(() => {
    if (typeof loadDataRef.current === 'function') {
      loadDataRef.current();
    }
  }, []);

  // Realtime: refetch as soon as DB changes
  const channels = realtimeTables?.length
    ? realtimeTables.map((tid) => createTableChannel(tid))
    : null;

  useRealtimeSubscription({
    channels,
    enabled: !!enabled && !!channels,
    debounce: 400,
    onEvent: refresh,
  });

  // Visibility + fallback poll
  useEffect(() => {
    if (!enabled || !loadData) return;

    const pollInterval = realtimeTables?.length ? 180000 : (intervalMs ?? 60000);

    const startPolling = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(refresh, pollInterval);
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh();
        startPolling();
      } else {
        stopPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadData, intervalMs, enabled, realtimeTables, refresh]);
};

export default useAutoRefresh;
